import {TITLE_MODEL} from '@app/titles/models/title';
import {TitlePoster} from '@app/titles/title-poster/title-poster';
import {TitleLink} from '@app/titles/title-link';
import {ChannelContentModel} from '@app/admin/channels/channel-content-config';
import {NEWS_ARTICLE_MODEL} from '@app/titles/models/news-article';
import {NewsArticleImage} from '@app/news/news-article-image';
import {getNewsArticleLink, NewsArticleLink} from '@app/news/news-article-link';
import {BulletSeparatedItems} from '@app/titles/bullet-separated-items';
import {FormattedDate} from '@common/i18n/formatted-date';
import {NewsArticleSourceLink} from '@app/news/news-article-source-link';
import {PERSON_MODEL} from '@app/titles/models/person';
import {PersonPoster} from '@app/people/person-poster/person-poster';
import {PersonLink} from '@app/people/person-link';
import {KnownForCompact} from '@app/people/known-for-compact';
import React, {useMemo} from 'react';
import {FormattedDuration} from '@common/i18n/formatted-duration';
import {InteractableRating} from '@app/reviews/interactable-rating';
import {Link} from 'react-router-dom';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import clsx from 'clsx';
import {useSelectedLocale} from '@common/i18n/selected-locale';

interface Props {
  item: ChannelContentModel;
}
export function ChannelContentListItem({item}: Props) {
  const {selectedTheme} = useThemeSelector();
  const {locale} = useSelectedLocale();

  switch (item.model_type) {
    case TITLE_MODEL:
      return (
        <div className="mb-24 flex items-start gap-24">
          <TitlePoster title={item} srcSize="md" size="w-128" showPlayButton />
          <div className="min-w-0 flex-auto pt-12">
            <TitleLink title={item} className="font-medium" />
            <BulletSeparatedItems className="mt-4 text-sm">
              {item.runtime ? (
                <FormattedDuration minutes={item.runtime} verbose />
              ) : null}
              {item.certification && (
                <span className="uppercase">{item.certification}</span>
              )}
            </BulletSeparatedItems>
            {item.rating && item.status !== 'upcoming' ? (
              <InteractableRating size="md" title={item} className="my-12" />
            ) : (
              <div className="my-12">
                <FormattedDate date={item.release_date} />
              </div>
            )}
            {item.description ? (
              <p className="text-sm">{item.description}</p>
            ) : null}
          </div>
        </div>
      );
    case PERSON_MODEL:
      return (
        <div className="mb-24 flex items-start gap-24">
          <PersonPoster person={item} srcSize="md" size="w-128" />
          <div className="min-w-0 flex-auto pt-12">
            <PersonLink person={item} className="block text-lg font-medium" />
            {item.primary_credit ? (
              <div className="mt-4 text-sm">
                <KnownForCompact person={item} />
              </div>
            ) : null}
            <p className="mt-12 text-sm">{item.description}</p>
          </div>
        </div>
      );
    case NEWS_ARTICLE_MODEL:
      return (
        <>
          <article>
            <div className="iq-blog-box">
              <NewsArticleImage
                article={item}
                className="mb-4 aspect-poster max-w-90"
              />
              <div className="iq-blog-detail">
                <div className="iq-blog-meta d-flex mb-3">
                  <ul className="list-inline mb-0">
                    <li className="border-gredient-left">
                      <FormattedDate date={item.created_at} />
                    </li>{' '}
                    <li className="border-gredient-left">
                      <NewsArticleSourceLink
                        article={item}
                        className={
                          selectedTheme.is_dark ? 'text-white' : 'text-black'
                        }
                      />
                    </li>
                  </ul>
                </div>
                <NewsArticleLink
                  article={item}
                  className={`font-medium ${selectedTheme.is_dark ? 'text-white' : 'text-black'}`}
                />
                <p className="line-count-2">{item.body}</p>
                <div className="iq-button">
                  <NewsArticleLink
                    article={item}
                    className={clsx(
                      `btn text-uppercase position-relative !flex w-fit items-center gap-2 hover:no-underline`,
                      locale.language === 'fa'
                        ? '!flex-row-reverse '
                        : 'flex-row',
                    )}
                  >
                    {'READ MORE'} <i className="fa-solid fa-play"></i>
                  </NewsArticleLink>
                </div>
              </div>
            </div>
          </article>{' '}
        </>
      );
    default:
      return null;
  }
}
